import React, { useCallback, useEffect, useState } from "react";
import { doc, getDoc, updateDoc, arrayUnion, arrayRemove } from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db } from '../../firebase';
import imageCompression from 'browser-image-compression';

import styleClasses from './css/MusicianGalleryPopup.module.scss';

import Loading from "../Loading";
import Popup from "../Popup";

function MusicianGalleryPopup({ uid, open, setOpenPopup, imageIndex, buttonFunction }) {
  //const [uid, setUid]                               = useState(null);
  const [active, setActive]                         = useState('');
  const [innerOpen, setInnerOpen]                   = useState(false);
  const [musicianGallery, setMusicianGallery]       = useState([]);
  const [webGallery, setWebGallery] = useState([]); // 새로 추가
  const [selectedBackground, setSelectedBackground] = useState('');
  const [popupTitle, setPopupTitle]                 = useState('');
  const [isLoading, setIsLoading]                   = useState(true);

  // 웹 이미지 URL 입력값
  const [webImageUrl, setWebImageUrl] = useState("");

  // 🔹 현재 로그인된 사용자 UID 가져오기
  /*
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setUid(user.uid);
      }
    });

    return () => unsubscribe();
  }, []);
  */

  // 🔹 뮤지션 갤러리 가져오기
  const getGallery = useCallback(async () => {
    if (!uid) {
      console.error("uid is not defined");
      setIsLoading(false);
      return;
    }

    const docRef = doc(db, "users_data", uid);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      setMusicianGallery(data.gallery || []);
      setWebGallery(data["web-gallery"] || []);  // web-gallery 필드 읽기
    } else {
      console.log("No such document!");
      setMusicianGallery([]);
      setWebGallery([]);
    }
    setIsLoading(false);
  }, [uid]);

  // 🔹 팝업 열기/닫기 + 뮤지션 갤러리 가져오기(열기)
  useEffect(() => {
    setActive(open ? styleClasses.active : '');
    if (open === true) {
      getGallery();
      // 초기화: 선택된 웹 URL 초기화
      setWebImageUrl("");
      const checkedImage = document.querySelector('input[name=musicianImage]:checked');
      if (checkedImage) { checkedImage.checked = false; }
    }
  }, [open, setOpenPopup, active, getGallery])

  // 🔹 이미지 업로드
  const uploadImage = async (event) => {
    if (musicianGallery.length > 9) {
      setPopupTitle('이미지는 최대 10개 까지 업로드 가능합니다.');
      setInnerOpen(true);
    } else {
      const file = event.target.files[0];
      if (!file) return;

      const allowedTypes = ['image/jpeg', 'image/png'];

      // 파일 확장자 확인 (jpg, png 제한)
      if (!allowedTypes.includes(file.type)) {
        setPopupTitle('이미지 형식이 올바르지 않습니다. <br>JPG 또는 PNG 형식의 이미지 파일만 업로드할 수 있습니다.');
        setInnerOpen(true);
        return;
      }

      setIsLoading(true);

      try {
        const options = {
          maxWidthOrHeight: 1000, // 해상도를 높여서 선명한 이미지 제공
          useWebWorker: true,
          maxSizeMB: 0.8,         // 파일 용량은 약간 높여서, 하지만 여전히 제한
          fileType: 'image/jpeg',
          initialQuality: 0.7,   // 품질과 용량 사이의 타협
        };
        const compressedFile = await imageCompression(file, options);
        
        // 압축 후 파일 용량 확인 (10MB 미만)
        const fileSizeMB = compressedFile.size / (1024 * 1024);
        if (fileSizeMB > 10) {
          setIsLoading(false);
          setPopupTitle("압축 후 파일 용량이 10MB를 초과했습니다. <br />더 작은 이미지를 사용해 주세요.");
          setInnerOpen(true);
          return;
        }

        const storage = getStorage();
        const storageRef = ref(storage, `users/${uid}/gallery/${file.name}`);
        const snapshot = await uploadBytes(storageRef, compressedFile);
        const downloadURL = await getDownloadURL(snapshot.ref);

        const docRef = doc(db, "users_data", uid);
  
        await updateDoc(docRef, {
          gallery: arrayUnion(downloadURL) // 새로운 값 추가
        });
        setMusicianGallery((prevCheckedIDs) => [...prevCheckedIDs, downloadURL]);
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
        console.error("파일 업로드 오류:", error);
        alert("파일 업로드 중 오류가 발생했습니다.");
      }
    }
  }

  // 🔹 이미지 복사 업로드
  const handlePaste = async (e) => {
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) {
          try {
            setIsLoading(true);
            const options = {
              maxWidthOrHeight: 1000,
              useWebWorker: true,
              maxSizeMB: 0.8,
              fileType: 'image/jpeg',
              initialQuality: 0.7,
            };
            const compressedFile = await imageCompression(file, options);
            
            // 고유한 파일 이름 생성 (예: 타임스탬프와 원래 파일명 결합)
            const uniqueFileName = `${Date.now()}_${file.name}`;
            const storage = getStorage();
            const storageRef = ref(storage, `users/${uid}/gallery/${uniqueFileName}`);
            const snapshot = await uploadBytes(storageRef, compressedFile);
            const downloadURL = await getDownloadURL(snapshot.ref);
    
            const docRef = doc(db, "users_data", uid);
            await updateDoc(docRef, {
              gallery: arrayUnion(downloadURL)
            });
            setMusicianGallery((prev) => [...prev, downloadURL]);
            setIsLoading(false);
            setPopupTitle('붙여넣은 이미지가 업로드되었습니다.');
            setInnerOpen(true);
          } catch (error) {
            setIsLoading(false);
            console.error("파일 업로드 오류:", error);
            alert("파일 업로드 중 오류가 발생했습니다.");
          }
        }
        break; // 하나의 이미지 처리
      }
    }
  };  
  

  // 🔹 웹 이미지 URL 저장 (주소 복사한 경우)
  const saveWebImageUrl = async () => {
    if (!webImageUrl.trim()) {
      setPopupTitle('이미지 URL을 입력해주세요.');
      setInnerOpen(true);
      return;
    }
    try {
      // Firestore 업데이트: musicians 문서의 web-gallery 필드에 추가
      const docRef = doc(db, "users_data", uid);
      await updateDoc(docRef, {
        "web-gallery": arrayUnion(webImageUrl.trim())
      });
      setWebGallery(prev => [...prev, webImageUrl.trim()]);
      // Optionally, you can maintain a local state for webGallery if needed.
    } catch (error) {
      console.error("웹 이미지 저장 오류:", error);
      alert("웹 이미지 저장 중 오류가 발생했습니다.");
    }
  };

  // 🔹 이미지 삭제
  const deleteImage = async (image) => {
    setIsLoading(true);

    const storage = getStorage();
  
    try {
      // 다운로드 URL에서 Storage 경로 추출
      const parts = image.split('/o/');
      if (parts.length < 2) {
        throw new Error("유효하지 않은 이미지 URL입니다.");
      }
      const fullPathWithQuery = parts[1];
      const pathEncoded = fullPathWithQuery.split('?')[0]; // query 파라미터 제거
      const filePath = decodeURIComponent(pathEncoded);
      
      // Storage에서 해당 파일 삭제
      const imageRef = ref(storage, filePath);
      await deleteObject(imageRef);
      console.log("Storage에서 이미지 삭제 성공:", filePath);
    } catch (error) {
      console.error("Storage에서 이미지 삭제 오류:", error);
      // 파일 삭제 실패 시에도 Firestore 업데이트는 진행할 수 있음
    }

    const docRef = doc(db, "users_data", uid);
    
    await updateDoc(docRef, { gallery: arrayRemove(image) });
    setMusicianGallery((previousGallery) =>
      previousGallery.includes(image)
        ? previousGallery.filter((previousImage) => previousImage !== image)
        : [...previousGallery, image]
    );
    setIsLoading(false);
  };

  const deleteWebImage = async (image) => {
    try {
      const docRef = doc(db, "users_data", uid);
      await updateDoc(docRef, { "web-gallery": arrayRemove(image) });
      setWebGallery(prev => prev.filter(img => img !== image));
    } catch (error) {
      console.error("웹 갤러리 이미지 삭제 오류:", error);
      alert("웹 갤러리 이미지 삭제 중 오류가 발생했습니다.");
    }
  };  

  // 🔹 선택한 이미지 적용
  const selectImage = () => {
    if (selectedBackground === '') {
      setPopupTitle('이미지를 선택해주세요.');
      setInnerOpen(true);
    } else {
      if (imageIndex === 4) {
        buttonFunction(`url("${ selectedBackground }")                no-repeat center / cover`);
      } else {
        buttonFunction(selectedBackground);
      }
    }
  }

  return (
    <>
      <div className={`${ styleClasses.backDrop } ${ active }`} onClick={ () => { setActive(''); setOpenPopup(false); } }/>
      <div className={`${ styleClasses.musicianGalleryPopup } ${ active }`} onPaste={handlePaste}>
        <div className={ styleClasses.inner }>
          <div className={ styleClasses.header }>
            <div className={ styleClasses.title }>
              <h1>이미지 업로드 / 선택</h1>
            </div>
            <div className={ styleClasses.closeButton }>
              <button onClick={ () => { setActive(''); setOpenPopup(false); } }>
                <div className="commonIcon">
                  <i className="closeWhite w32"></i>
                </div>
              </button>
            </div>
          </div>
          <div className={`${ styleClasses.content } ${ isLoading ? styleClasses.noScroll : undefined }`}>
            <p>복사한 웹 이미지 주소는 갯수 제한 없이 저장할 수 있습니다. 단, 저작권에 유의해주세요.</p>
            <div className={ styleClasses.inputContent }>
              <div className={ styleClasses.icon }>
                <img src="/assets/icons/link-icon.png" alt="Link Icon" />
              </div>
              <input type="text" placeholder="웹 이미지 URL 붙여넣기" value={ webImageUrl } onChange={(e) => setWebImageUrl(e.target.value)} />
              <div className={ styleClasses.icon }>
                <img src="/assets/icons/plus-white-icon.png" alt="Plus Icon" onClick={saveWebImageUrl}/>
              </div>
            </div>
            <div className={ styleClasses.imageContent }>
              {webGallery.map((image, index) => (
                <label htmlFor={`musicianImage${index}`} key={index}>
                  <input type="radio" name="musicianImage" id={`musicianImage${index}`} value={ image } onChange={(e) => setSelectedBackground(e.target.value)} />
                  <img src={ image } alt={`Web Gallery ${index}`} onError={(e) => { e.target.onerror = null; e.target.src = '/assets/images/image_error.svg'; }} />
                  <button onClick={() => deleteWebImage(image)}>
                    <div className="commonIcon">
                      <i className="trashWhite w24"></i>
                    </div>
                  </button>
                </label>
              ))}
            </div>
            <hr />
            <p>이미지를 복사하거나 업로드해주세요. 최대 10개까지 업로드 가능합니다.</p>
            <div className={ styleClasses.imageContent }>
              <label htmlFor='uploadImage' className={ styleClasses.imageUploadLabel }>
                {
                  musicianGallery.length > 9
                  ?
                  <div className={ styleClasses.imageUploadAlert }>
                    이미지는 최대 10개까지 <br/> 업로드 가능합니다.
                  </div>
                  :
                  (
                    <>
                      <div className="commonIcon">
                        <i className="plusBlack w32"></i>
                      </div>
                      <input type="file" name="uploadImage" id="uploadImage" onChange={ (event) => uploadImage(event) }/>
                      <p className={ musicianGallery.length > 9 ? styleClasses.maximum : undefined }>
                        <span>{ musicianGallery.length } / 10</span>
                      </p>
                    </>
                  )
                }
              </label>
              {
                musicianGallery.map((image, index) => (
                  <label htmlFor={`musicianImage${ index }`} key={ index }>
                    <input type="radio" name="musicianImage" id={`musicianImage${ index }`} value={ image } onChange={ (e) => setSelectedBackground(e.target.value) } />
                    <img src={ image } alt={`Musician Gallery ${ index }`} />
                    <button onClick={ () => deleteImage(image) }>
                      <div className="commonIcon">
                        <i className="trashWhite w24"></i>
                      </div>
                    </button>
                  </label>
                ))
              }
              {
                isLoading
                &&
                (
                  <>
                    <div className={ styleClasses.loadingBackdrop }></div>
                    <Loading />
                  </>
                )
              }
            </div>
          </div>
          <div className={ styleClasses.footer }>
            <div className={`commonButton fullWidth ${ styleClasses.w160 }`}>
              <button onClick={ () => { setActive(''); setOpenPopup(false); } }>닫기</button>
            </div>
            <div className={`commonButton fullWidth ${ styleClasses.w160 }`}>
              <button className="black" onClick={ selectImage }>선택</button>
            </div>
          </div>
        </div>
      </div>
      <Popup open={ innerOpen } setOpenPopup={ setInnerOpen } title={ popupTitle } />
    </>
  );
}

export default MusicianGalleryPopup;